import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const DashboardChart = ({ alerts }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const data = [
    { name: 'Expired', value: alerts.default?.length || 0, color: '#6B7280', description: 'Past due alerts' },
    { name: 'Due Today', value: alerts.danger?.length || 0, color: '#EF4444', description: 'Alerts due in 24 hours' },
    { name: 'Due Soon', value: alerts.alert?.length || 0, color: '#EAB308', description: 'Alerts due in 72 hours' },
    { name: 'Upcoming', value: alerts.warning?.length || 0, color: '#F97316', description: 'Future alerts' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = data.find(d => d.name === label);
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 max-w-[200px]">
          <p className="font-semibold text-gray-800 text-sm">{label}</p>
          <p className="text-gray-600 text-xs">{item.description}</p>
          <p className="font-bold mt-1 text-sm" style={{ color: item.color }}>
            {payload[0].value} alerts
          </p>
        </div>
      );
    }
    return null;
  };

  const Legend = () => (
    <div className={`flex flex-wrap gap-2 ${isMobile ? 'mt-4' : ''}`}>
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <div 
            className="w-2 h-2 rounded-full" 
            style={{ backgroundColor: item.color }}
          />
          <span className="text-xs text-gray-600">{item.name}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full bg-white rounded-xl shadow-lg p-4 md:p-8">
      <div className={`${isMobile ? 'flex flex-col' : 'flex justify-between items-center'} mb-6`}>
        <h3 className="text-lg md:text-xl font-bold text-gray-800">Alert Distribution</h3>
        {!isMobile && <Legend />}
      </div>
      
      <div className="h-60 md:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={isMobile ? 
              { top: 20, right: 10, left: 0, bottom: 20 } : 
              { top: 20, right: 30, left: 20, bottom: 20 }
            }
            barSize={isMobile ? 30 : 50}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ 
                fill: '#6B7280',
                fontSize: isMobile ? 10 : 12,
                angle: isMobile ? -45 : 0,
                textAnchor: isMobile ? 'end' : 'middle',
                dy: isMobile ? 10 : 0
              }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ 
                fill: '#6B7280',
                fontSize: isMobile ? 10 : 12
              }}
              width={isMobile ? 30 : 40}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            />
            <Bar 
              dataKey="value" 
              radius={[4, 4, 0, 0]}
              animationDuration={1500}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {isMobile && <Legend />}
    </div>
  );
};

export default DashboardChart;